import _ from 'lodash';
import {
  CandidateMessageWithMetadata,
  ContextType,
  InteractionStepType,
  ModelResponseEditorStepResponse,
  ModelResponseSelectorStepResponse,
  StepParamsForType,
} from '@scale/shared/types/chat';
import { StepResponsePair } from '../InteractionStep';
import { Message } from '@scale/shared/types/chat-completion';
import { IMultiTurnThreadHistory } from '../ThreadHistory.interface';

export interface ModelResponseCentricColumn {
  id: string;
  turn: number;
  stepParams: StepParamsForType<InteractionStepType>;
  messages: Message[];
  response: Message | null;
  metadata: { [k: string]: any };
  annotations: StepResponsePair[];
  rewrite?: string;
  isGoldenPath: boolean;
  beforeStepsAndResponses: StepResponsePair[];
  afterStepsAndResponses: StepResponsePair[];
}

function getCandidateMetadata(
  response: ModelResponseSelectorStepResponse,
  candidate: CandidateMessageWithMetadata,
) {
  const displayIndex = response.context.displayOrder.indexOf(candidate.index);
  return {
    'Model Endpoint': candidate.endpoint?.id,
    'Model URL': candidate.endpoint?.url,
    'Display Name': `Response ${displayIndex + 1}`,
  };
}

function createCandidateColumn(
  stepParams: StepParamsForType<InteractionStepType>,
  response: ModelResponseSelectorStepResponse,
  candidate: CandidateMessageWithMetadata,
  turnIndex: number,
  beforeStepsAndResponses: StepResponsePair[],
): ModelResponseCentricColumn {
  const metadata = getCandidateMetadata(response, candidate);
  return {
    id: candidate.id,
    turn: turnIndex,
    stepParams,
    messages: response.context.messages,
    response: candidate.message,
    metadata,
    annotations: [],
    isGoldenPath: candidate.id === response.context.selectedId,
    beforeStepsAndResponses,
    afterStepsAndResponses: [],
  };
}

function createEditorColumn(
  stepParams: StepParamsForType<InteractionStepType>,
  response: ModelResponseEditorStepResponse,
  history: IMultiTurnThreadHistory,
  turnIndex: number,
  beforeStepsAndResponses: StepResponsePair[],
): ModelResponseCentricColumn {
  const messages = history.getChatHistory();
  return {
    id: 'Editor Step',
    turn: turnIndex,
    stepParams,
    messages,
    response: null,
    metadata: {},
    annotations: [],
    rewrite: response.output,
    isGoldenPath: true,
    beforeStepsAndResponses,
    afterStepsAndResponses: [],
  };
}

export function transformToResponseCentricView(threadHistory: IMultiTurnThreadHistory) {
  const { before, turns, after } = threadHistory.getStepsWithResponses();

  // We create a column approximately for every model response, the before steps,
  // and the after steps of the thread.
  const columns: ModelResponseCentricColumn[] = [];
  const isSingleTurn = turns.length === 1;
  let turnIndex = 0;

  for (const turn of turns) {
    const turnColumns: ModelResponseCentricColumn[] = [];
    let beforeStepsAndResponses: StepResponsePair[] = [];
    let currentSelectedColumn: ModelResponseCentricColumn | null = null;

    const turnStepsAndResponses = _.zip(turn.steps, turn.responses);
    for (const [stepParams, response] of turnStepsAndResponses) {
      // Only show completed steps.
      if (!stepParams || !response || response.contextType === ContextType.Skipped) {
        continue;
      }
      const type = stepParams.type;
      // Model Response Selector: Determine which candidates have been presented to the
      // viewer and create a new column for each.
      if (type === InteractionStepType.ModelResponseSelector) {
        const candidates = response.context?.candidates || [];
        const orderedCandidates = _.sortBy(candidates, c =>
          response.context.displayOrder.indexOf(c.index),
        );
        for (const candidate of orderedCandidates) {
          const isSelected = response.context.selectedId === candidate.id;
          const column = createCandidateColumn(
            stepParams,
            response as ModelResponseSelectorStepResponse,
            candidate,
            turnIndex,
            beforeStepsAndResponses,
          );
          if (isSelected) {
            currentSelectedColumn = column;
          }
          turnColumns.push(column);
        }
        // Once we have attached the before steps, we can reset them.
        beforeStepsAndResponses = [];
      }
      // Text Collection Per Response: Append the per-candidate response to
      // the correct column matching the candidate ID
      else if (type === InteractionStepType.TextCollectionPerResponse) {
        if (turnColumns === null) {
          throw new Error(
            'Invalid configuration: per-response text collection missing selector step',
          );
        }
        turnColumns.forEach(column => {
          const annotations = _.get(response.context.annotations, column.id);
          column.annotations.push([stepParams, annotations]);
        });
      }
      // Model Response Editor: If there are edits to the last model response, then
      // append them to the currently selected column (golden path).
      else if (type === InteractionStepType.ModelResponseEditor && currentSelectedColumn) {
        currentSelectedColumn.rewrite = response.output;
      }
      // Model Response Editor: If there is no previous model response, then create
      // a new column just for this. This is an initial draft (instruction following).
      else if (type === InteractionStepType.ModelResponseEditor) {
        const column = createEditorColumn(
          stepParams,
          response as ModelResponseEditorStepResponse,
          threadHistory.until(response.index),
          turnIndex,
          beforeStepsAndResponses,
        );
        turnColumns.push(column);
      }
      // All other steps get appended as is to the column. If there is only one turn,
      // then just append before and after steps to the first and last column.
      else if (isSingleTurn) {
        if (turnColumns.length === 0) {
          before.push([stepParams, response]);
        } else {
          after.unshift([stepParams, response]);
        }
      }
      // Otherwise, append the before and after steps to the currently selected
      // column, or stage them in the before steps if there is no selected column.
      else if (currentSelectedColumn) {
        currentSelectedColumn.afterStepsAndResponses.push([stepParams, response]);
      } else {
        beforeStepsAndResponses.push([stepParams, response]);
      }
    }
    turnIndex++;
    columns.push(...turnColumns);
  }
  return { before, columns, after };
}
